import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post } from '@/shared/api/api-client';
import { refusalMessage } from '../ambient/decision-identity';
import type {
  AmbientTranscriptPort,
  AmbientTranscriptRecord,
  RecordTranscriptBody,
  ReviseTranscriptBody,
  TranscriptCorrectionAction,
} from '../ambient/transcript-persistence-types';

// PROD-1994 / PROD-1995 — the Scrips.Patient ambient transcript endpoints:
//
//   GET  api/Patients/{patientId}/Encounters/{encounterId}/AmbientTranscript
//   POST api/Patients/{patientId}/Encounters/{encounterId}/AmbientTranscript/Segments
//   POST api/Patients/{patientId}/Encounters/{encounterId}/AmbientTranscript/Revisions
//   PATCH api/Patients/{patientId}/Encounters/{encounterId}/AmbientTranscript/Corrections/{correctionId}  (PROD-3195)
//
// Each POST answers with the whole transcript as the server now holds it, which is written straight
// into the query cache — so a remount (tab switch, navigation) restores from what the server
// confirmed, not from a guess. Errors are NOT caught here: they propagate to the panel, which shows them.

const base = (patientId: string, encounterId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}/AmbientTranscript`;

export const ambientTranscriptKey = (patientId?: string | null, encounterId?: string | null) =>
  ['ambient-transcript', patientId, encounterId] as const;

/**
 * The port the ambient panel saves the transcript through. `undefined` until the patient and the
 * encounter are both known — the panel then keeps edits in memory for the session only.
 */
export function useAmbientTranscriptPort(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
): AmbientTranscriptPort | undefined {
  const queryClient = useQueryClient();
  const key = ambientTranscriptKey(patientId, encounterId);
  const query = useQuery<AmbientTranscriptRecord>({
    queryKey: key,
    enabled: !!patientId && !!encounterId,
    // The transcript is PHI and only changes through this port: never refetch it behind the editor.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) =>
      await get<AmbientTranscriptRecord>(base(patientId as string, encounterId as string), { signal }),
  });
  const data = query.data;
  const readError = query.error ? refusalMessage(query.error) : null;

  return useMemo(() => {
    if (!patientId || !encounterId) return undefined;
    const cacheKey = ambientTranscriptKey(patientId, encounterId);
    const store = (record: AmbientTranscriptRecord) => {
      queryClient.setQueryData(cacheKey, record);
      return record;
    };
    return {
      transcript: data,
      readError,
      recordSegments: async (body: RecordTranscriptBody) =>
        store(await post<AmbientTranscriptRecord>(`${base(patientId, encounterId)}/Segments`, body)),
      revise: async (body: ReviseTranscriptBody) =>
        store(await post<AmbientTranscriptRecord>(`${base(patientId, encounterId)}/Revisions`, body)),
      refresh: async () => store(await get<AmbientTranscriptRecord>(base(patientId, encounterId))),
      actOnCorrection: async (correctionId: string, action: TranscriptCorrectionAction) =>
        store(
          await patch<AmbientTranscriptRecord>(
            `${base(patientId, encounterId)}/Corrections/${encodeURIComponent(correctionId)}`,
            { action },
          ),
        ),
    };
  }, [patientId, encounterId, data, readError, queryClient]);
}
